/**
 * The handler for a SET (D217) — the one action the engine takes on several
 * things at once, looking at them together.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot } from '../defaults/default-responses.js';
import { resolveConditionalText } from '../narrative/conditional-text.js';
import { type ActionHandlerResult, completed, completedSpeaking } from './action-handler-result.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';

/**
 * `inspect_set` (D217) — looking at a SET a plural named, answered in one
 * line under the pack's `set` policy.
 *
 * @remarks
 * When every member belongs to ONE group, the group's description speaks —
 * conditional text like any description, so the jar house says one thing
 * while all four jars stand open and another as they are sealed. Otherwise
 * the engine names the members in one sentence (`pluralSetNamed`), in the
 * order the parser found them, joined as English joins a list. Looking at a
 * set reads no log and marks nothing: a document is read one at a time.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param action - The set to look at
 * @param cursor - Where the line lands
 * @returns The one line, as a completed turn
 */
export const handleInspectSet = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'inspect_set' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const members = action.itemIds.map((itemId) => requireItem(bundle, itemId));
	const sharedGroupIds = new Set(members.map((member) => member.groupId));
	const [onlyGroupId] = sharedGroupIds;
	const group =
		sharedGroupIds.size === 1 && onlyGroupId !== undefined
			? bundle.contentPack.groups.find((candidate) => candidate.groupId === onlyGroupId)
			: undefined;
	const triggerContext = `inspect_set:${action.itemIds.join('+')}`;

	if (group !== undefined) {
		return completed(
			state,
			stampNarrativeLines(
				bundle,
				state,
				cursor,
				[{ priority: 'standard', source: 'environment', text: resolveConditionalText(group.description, bundle, state) }],
				triggerContext,
			),
		);
	}

	const itemNames = namesAsEnglishList(members.map((member) => member.name));

	return completedSpeaking(bundle, state, cursor, [speakSlot(bundle, state, 'pluralSetNamed', { itemNames })], triggerContext);
};

/**
 * Names joined as English joins a list — `a`, `a and b`, `a, b and c` — with
 * the first letter raised, because the list opens its own sentence.
 *
 * @param names - Two or more names, in order
 * @returns The list
 */
const namesAsEnglishList = (names: readonly string[]): string => {
	const list = names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names.slice(-1).join('')}`;

	return `${list.charAt(0).toUpperCase()}${list.slice(1)}`;
};
